/**
 * Pure helpers for the Safety Tables drawer / grid (Phase 5 port of V1 Safety
 * "Table Maintenance"). The drawer keeps the loaded record as `initial` and diffs the
 * edited copy on Save: the engine accepts ONLY editable fields that changed (a read-only
 * field echoed back is a 422), so nothing here ever emits `editable=false` columns.
 */
import { createElement } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type {
  SafetyCategoryOption, SafetyLookupOption, SafetyLookupOptions, SafetyTableChildSpec, SafetyTableField, SafetyTableOptions, SafetyTableSpec,
} from '../../../types/safetySystem';

/**
 * ISO `YYYY-MM-DD[ HH:MM]` -> `MM/DD/YYYY[ HH:MM]`; zero dates -> ''. Same rule as
 * `safetyDrawerShared.fmtDateTime`, kept local so this module stays free of the component kit.
 */
export const fmtDate = (v: unknown): string => {
  const s = norm(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(s);
  if (!m) return s;
  if (m[1] === '0000') return '';
  return m[4] ? `${m[2]}/${m[3]}/${m[1]} ${m[4]}:${m[5]}` : `${m[2]}/${m[3]}/${m[1]}`;
};

export type FormValues = Record<string, unknown>;
/** A child row in the editor: the server row (+ `recno`) or a new row (`_key` only). */
export type ChildFormRow = Record<string, unknown> & { _key?: string; recno?: number };

/** null / undefined and '' are the same "empty" on the wire. */
export const norm = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/**
 * Editable on the current form. A `create_only` field (which the catalog also marks
 * `editable: true`) is only editable while creating; `ssn` is handled by the drawer.
 */
export const isEditableField = (f: SafetyTableField, isCreate: boolean): boolean =>
  f.kind !== 'ssn' && (f.create_only ? isCreate : f.editable);

const decimalText = (v: unknown): string => {
  const s = norm(v).trim();
  if (s === '') return '';
  const n = Number(s);
  return Number.isFinite(n) ? n.toFixed(2) : s;
};

/** Blank per kind: '' / null / undefined, and 0 on a lookup (V1 stores 0 for "none"). */
export function isBlankValue(f: SafetyTableField, v: unknown): boolean {
  const s = norm(v).trim();
  if (s === '') return true;
  if (f.kind === 'lookup' && (s === '0' || s === '0.0')) return true;
  return false;
}

/** The stored value normalised for the form (what the inputs hold and what the diff compares against). */
function formValue(f: SafetyTableField, raw: unknown): unknown {
  if (raw === null || raw === undefined) return f.kind === 'yn' ? 'N' : '';
  if (f.kind === 'decimal') return decimalText(raw);
  if (f.kind === 'lookup' && f.fk_zero_blank && isBlankValue(f, raw)) return '';
  if (f.kind === 'yn') return norm(raw).toUpperCase() === 'Y' ? 'Y' : 'N';
  return raw;
}

/**
 * Initial form values: every spec field (read-only ones too, for display) plus the
 * `<lookup>_label` keys the server sent. A new record is blank per kind (`yn` = 'N',
 * the engine's create default).
 */
export function initialValues(spec: SafetyTableSpec, row: Record<string, unknown> | null): FormValues {
  const out: FormValues = {};
  for (const f of spec.fields) {
    out[f.name] = formValue(f, row ? row[f.name] : null);
    if (f.kind === 'lookup') out[`${f.name}_label`] = row ? norm(row[`${f.name}_label`]) : '';
  }
  return out;
}

/** Form value -> wire value by kind. */
export function wireValue(f: SafetyTableField, v: unknown): unknown {
  switch (f.kind) {
    case 'yn': return norm(v).toUpperCase() === 'Y' ? 'Y' : 'N';
    case 'decimal': return decimalText(v);
    case 'date': return norm(v).trim().slice(0, 10);
    case 'datetime': return norm(v).trim().replace('T', ' ').slice(0, 16);
    case 'lookup': return f.fk_zero_blank && isBlankValue(f, v) ? '' : v;
    default: return v;
  }
}

const same = (f: SafetyTableField, a: unknown, b: unknown): boolean => norm(wireValue(f, a)) === norm(wireValue(f, b));

/**
 * The `row` body: on edit the editable fields whose value changed (never read-only,
 * never `create_only`); on create every editable field with a non-blank value plus the
 * `yn` flags (always 'Y'/'N'). `ssn` fields are excluded — the drawer appends a typed value.
 */
export function changedRow(spec: SafetyTableSpec, initial: FormValues, current: FormValues, isCreate: boolean): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of spec.fields) {
    if (!isEditableField(f, isCreate)) continue;
    const v = current[f.name];
    if (isCreate) {
      if (f.kind === 'yn' || !isBlankValue(f, v)) out[f.name] = wireValue(f, v);
      continue;
    }
    if (f.create_only) continue;
    if (!same(f, initial[f.name], v)) out[f.name] = wireValue(f, v);
  }
  return out;
}

/** V1's client check, server-enforced too: "<label> can not be blank." for each blank required editable field. */
export function requiredErrors(spec: SafetyTableSpec, values: FormValues, isCreate = false): string[] {
  const out: string[] = [];
  for (const f of spec.fields) {
    if (!f.required || !(f.editable || f.create_only || isCreate)) continue;
    if (f.kind === 'ssn') continue;
    if (isBlankValue(f, values[f.name])) out.push(`${f.label} can not be blank.`);
  }
  return out;
}

const childFields = (child: SafetyTableChildSpec): SafetyTableField[] =>
  child.fields.filter((f) => f.name !== child.soft_delete && f.editable);

const isDeletedRow = (child: SafetyTableChildSpec, row: Record<string, unknown>): boolean =>
  !!child.soft_delete && norm(row[child.soft_delete]).toUpperCase() === 'Y';

/**
 * The array to post for one managed child: new rows (no recno) with their non-blank
 * fields, existing rows that changed as `{recno, ...changed}`, and a flipped soft flag as
 * `{recno, deleted}`. New rows removed before saving (or left completely blank) are dropped;
 * a child without a soft flag never posts `deleted`.
 */
export function childDiff(child: SafetyTableChildSpec, initialRows: Record<string, unknown>[], rows: ChildFormRow[]): Record<string, unknown>[] {
  const byRecno = new Map<number, Record<string, unknown>>(initialRows.map((r) => [Number(r.recno), r]));
  const fields = childFields(child);
  const out: Record<string, unknown>[] = [];
  for (const row of rows) {
    const recno = Number(row.recno || 0);
    if (!recno) {
      if (isDeletedRow(child, row)) continue;
      const posted: Record<string, unknown> = {};
      for (const f of fields) if (!isBlankValue(f, row[f.name])) posted[f.name] = wireValue(f, row[f.name]);
      if (Object.keys(posted).length) out.push(posted);
      continue;
    }
    const orig = byRecno.get(recno) ?? {};
    const posted: Record<string, unknown> = { recno };
    for (const f of fields) if (!same(f, orig[f.name], row[f.name])) posted[f.name] = wireValue(f, row[f.name]);
    if (child.soft_delete && isDeletedRow(child, row) !== isDeletedRow(child, orig)) posted[child.soft_delete] = isDeletedRow(child, row) ? 'Y' : 'N';
    if (Object.keys(posted).length > 1) out.push(posted);
  }
  return out;
}

export const rowsDirty = (child: SafetyTableChildSpec, initialRows: Record<string, unknown>[], rows: ChildFormRow[]): boolean =>
  childDiff(child, initialRows, rows).length > 0;

// ---- child editor rows -------------------------------------------------------

let childSeq = 0;
export const newChildKey = (): string => `new-${Date.now()}-${++childSeq}`;

/** The local identity of a child row (`_key` for new rows, else its recno). */
export const rowKey = (row: ChildFormRow): string => String(row._key ?? row.recno ?? '');

/** A blank row for the child: '' per field, `yn` = 'N', soft flag 'N'. */
export function newChildRow(child: SafetyTableChildSpec): ChildFormRow {
  const row: ChildFormRow = { _key: newChildKey() };
  for (const f of child.fields) row[f.name] = f.kind === 'yn' ? 'N' : '';
  if (child.soft_delete) row[child.soft_delete] = 'N';
  return row;
}

/** Server child rows -> editor rows (keyed by recno). */
export const withChildKeys = (rows: Record<string, unknown>[]): ChildFormRow[] =>
  rows.map((r) => ({ ...r, _key: String(r.recno) }));

// ---- drawer constants / option lists ----------------------------------------

export const SENSITIVE_COMPONENT_ID = 'safety-system-payroll-sensitive';
export const tableComponentId = (key: string) => `safety-system-table-${key}`;

/** The per-field lookup lists of an `options` payload (spec extras such as `categories_by_type` dropped). */
export function lookupOptions(options: SafetyTableOptions | undefined): SafetyLookupOptions {
  const out: SafetyLookupOptions = {};
  for (const [k, v] of Object.entries(options ?? {})) if (Array.isArray(v)) out[k] = v;
  return out;
}

/** Blank element rows for a NEW survey of type `typeRecno`, from `options.categories_by_type` (one per active category). */
export function newElementRows(options: SafetyTableOptions | undefined, typeRecno: string): Record<string, unknown>[] {
  const cats: SafetyCategoryOption[] = options?.categories_by_type?.[typeRecno] ?? [];
  return cats.map((c) => ({ recno: 0, surveycategoryrecno: c.surveycategoryrecno, category: c.category, elementrating: '0.0', elementcomments: '' }));
}

/** Choices for a `select` / non-driver `lookup`, keeping an off-list stored value selectable (`allow_other`, legacy data). */
export function selectOptionsFor(
  f: SafetyTableField, values: FormValues, options: SafetyLookupOptions,
): { value: string; label: string }[] {
  const current = norm(values[f.name]);
  if (f.kind === 'select') {
    const opts = (f.options ?? []).filter((o) => o !== '').map((o) => ({ value: o, label: o }));
    if (current && !opts.some((o) => o.value === current)) opts.unshift({ value: current, label: current });
    return opts;
  }
  const opts = (options[f.name] ?? []).map((o) => ({ value: String(o.value), label: o.label }));
  if (current && current !== '0' && !opts.some((o) => o.value === current)) {
    opts.unshift({ value: current, label: norm(values[`${f.name}_label`]) || current });
  }
  return opts;
}

// ---- survey elements (unmanaged child: one row per active category) --------

export const RATING_MIN = 0;
export const RATING_MAX = 10;

const ratingText = (v: unknown): string => {
  const s = norm(v).trim();
  if (s === '') return '0.0';
  const n = Number(s);
  return Number.isFinite(n) ? n.toFixed(1) : s;
};

/** Every element row in full: the backend upserts one row per category (recno 0 = insert). */
export const elementsPayload = (rows: Record<string, unknown>[]): Record<string, unknown>[] =>
  rows.map((r) => ({
    recno: Number(r.recno || 0),
    surveycategoryrecno: r.surveycategoryrecno,
    elementrating: ratingText(r.elementrating),
    elementcomments: norm(r.elementcomments),
  }));

const ONE_DECIMAL = /^\d+(\.\d)?$/;

/** 0–10 with at most one decimal (the column is decimal(4,1); V1 never rounded for the user). */
export function ratingErrors(rows: Record<string, unknown>[]): string[] {
  const out: string[] = [];
  for (const r of rows) {
    const cat = norm(r.category) || 'Element';
    const s = norm(r.elementrating).trim() || '0';
    const n = Number(s);
    if (!Number.isFinite(n) || n < RATING_MIN || n > RATING_MAX) out.push(`${cat} rating must be between ${RATING_MIN} and ${RATING_MAX}.`);
    else if (!ONE_DECIMAL.test(s)) out.push(`${cat} rating must have at most one decimal.`);
  }
  return out;
}

/** DB datetime `YYYY-MM-DD HH:MM[:SS]` -> `datetime-local` input value `YYYY-MM-DDTHH:MM`. */
export const toInputDateTime = (v: unknown): string => norm(v).trim().replace(' ', 'T').slice(0, 16);

/** Mean of the element ratings to 1 decimal (what the server stores as `surveyrating`); '' with no rows. */
export function meanRating(rows: Record<string, unknown>[]): string {
  if (!rows.length) return '';
  const total = rows.reduce((acc, r) => acc + (Number(norm(r.elementrating).trim() || '0') || 0), 0);
  return (total / rows.length).toFixed(1);
}

// ---- grid ------------------------------------------------------------------

export const humanize = (name: string): string =>
  name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Cell text for a field of a server row (lookups show the label the server resolved). */
export function displayValue(f: SafetyTableField, row: Record<string, unknown>): string {
  const v = row[f.name];
  switch (f.kind) {
    case 'yn': return norm(v) === '' ? '' : norm(v).toUpperCase() === 'Y' ? 'Yes' : 'No';
    case 'date':
    case 'datetime': return fmtDate(v);
    case 'decimal': return decimalText(v);
    case 'lookup': return norm(row[`${f.name}_label`]) || (f.fk_zero_blank && isBlankValue(f, v) ? '' : norm(v) === '0' ? '' : norm(v));
    default: return norm(v);
  }
}

const RIGHT = new Set(['int', 'decimal']);

/** TanTable columns: `in_grid` fields (lookups keyed by `<name>_label`) followed by the computed columns. */
export function gridColumns<T extends Record<string, unknown>>(spec: SafetyTableSpec): ColumnDef<T>[] {
  const cols: ColumnDef<T>[] = spec.fields.filter((f) => f.in_grid).map((f) => ({
    id: f.name,
    accessorKey: f.kind === 'lookup' ? `${f.name}_label` : f.name,
    header: f.label,
    size: Math.max(80, (f.width || 14) * 8),
    cell: ({ row }) => {
      const text = displayValue(f, row.original);
      return RIGHT.has(f.kind) ? createElement('span', { className: 'block text-right tabular-nums' }, text) : text;
    },
  } as ColumnDef<T>));
  for (const name of spec.computed) {
    cols.push({ id: name, accessorKey: name, header: humanize(name), size: 160, cell: ({ getValue }) => norm(getValue()) } as ColumnDef<T>);
  }
  return cols;
}
